import React, { useRef, useState } from 'react';
import { CheckCircle2, FileJson, Loader2, MinusCircle, TriangleAlert, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { ewbApi } from '@/services/ewayBillApi';
import { slug } from '@/components/ewayBill/TallyNames';

const RES = {
  updated: ['now Active', 'text-emerald-700', CheckCircle2], created: ['new WMS record', 'text-teal-700', CheckCircle2],
  already: ['already in WMS', 'text-gray-500', MinusCircle], waiting: ['not in Tally yet', 'text-amber-700', TriangleAlert],
  conflict: ['check this', 'text-rose-700', TriangleAlert], rejected: ['portal error', 'text-rose-700', TriangleAlert],
};

const ImportResult = ({ r, onClose }) => (
  <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="portal-import-result">
      <DialogHeader><DialogTitle>Portal results imported — {r?.file}</DialogTitle></DialogHeader>
      {r && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
            {Object.entries(RES).map(([k, [l, cls]]) => (
              <div key={k} className="rounded-lg bg-slate-50 px-2 py-2"><div className={`text-lg font-bold tabular-nums ${cls}`} data-testid={`portal-import-count-${k}`}>{r.counts[k]}</div>
                <div className="text-[11px] text-gray-600">{l}</div></div>
            ))}
          </div>
          <ul className="divide-y rounded-lg border border-gray-200 text-sm" data-testid="portal-import-rows">
            {r.rows.map((x) => {
              const [l, cls, Icon] = RES[x.result];
              return (
                <li key={`${x.doc_no}-${x.ewb_no}`} className="flex items-start gap-2 px-3 py-2" data-testid={`portal-import-row-${slug(x.doc_no)}`}>
                  <Icon className={`mt-0.5 w-4 h-4 shrink-0 ${cls}`} />
                  <div className="min-w-0"><span className="font-mono font-semibold">{x.doc_no}</span>{x.ewb_no && <span className="ml-2 font-mono text-gray-600">EWB {x.ewb_no}</span>}
                    <div className={`text-xs ${cls}`}>{l} — {x.note}</div></div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

/** Ticked waiting bills → portal Bulk Generation JSON; the portal's results file (GridViewExport) → EWB No back into WMS. */
export const PortalJsonBar = ({ rows, picked, setPicked, onDone, waiting }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState(null);
  const download = () => {
    setBusy('json');
    ewbApi.portalJson([...picked]).then((n) => { toast.success(`Portal JSON with ${n} e-way bill(s) downloaded — upload it on the portal: e-Waybill → Bulk Generation`); setPicked(new Set()); onDone(); })
      .catch((e) => toast.error(errMsg(e, 'Download failed'))).finally(() => setBusy(''));
  };
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy('import');
    ewbApi.portalImport(f).then((r) => { setRes(r); onDone(); }).catch((x) => toast.error(errMsg(x, 'Import failed'), { duration: 8000 })).finally(() => setBusy(''));
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50/50 px-3 py-2" data-testid="portal-json-bar">
      <Button size="sm" variant="outline" onClick={() => setPicked(new Set(waiting.map((b) => b.id)))} disabled={!waiting.length} data-testid="ewb-pick-all-waiting">
        Select all waiting ({waiting.length})</Button>
      {picked.size > 0 && <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())} data-testid="ewb-pick-clear">Clear</Button>}
      <Button size="sm" onClick={download} disabled={!picked.size || !!busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ewb-portal-json-btn">
        {busy === 'json' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileJson className="w-4 h-4 mr-1" />}Download portal JSON ({picked.size})</Button>
      <input ref={ref} type="file" accept=".xls,.xlsx,.csv,.htm,.html" className="hidden" onChange={pick} data-testid="ewb-portal-import-input" />
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={!!busy} data-testid="ewb-portal-import-btn">
        {busy === 'import' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Import portal results (.xls)</Button>
      <span className="text-xs text-gray-600">Upload the JSON on the portal (Bulk Generation), then import the results file the portal gives you.</span>
      <ImportResult r={res} onClose={() => setRes(null)} />
    </div>
  );
};

export default PortalJsonBar;
